/** Pairwise census reads a wrapping size run as banded; `alongShelfShare` measures it directly. */
import { useMemo } from "react";
import { inferCdt, type InferenceReport } from "./cdtInfer";
import {
  summarizeInference,
  type ContraryBlockJudge,
  type InferenceJudges,
  type InferenceSummary,
  type LevelOrientationJudge,
} from "./inferenceExceptions";
import type { ResolverInputs } from "./toResolverInputs";

export interface InferenceApi extends InferenceSummary {
  report: InferenceReport | null;
}

const EMPTY: InferenceSummary = { confidenceByAttr: {}, exceptions: [] };

const PATH_SEPARATOR = " › ";
const ALONG_SHELF_SHARE = 0.5;

interface JudgePlacement {
  run: number;
  shelf: number;
  width: number;
  path: string[];
}

const placementsOf = (inputs: ResolverInputs): JudgePlacement[] => {
  const attrsById = new Map(inputs.itemsDoc.ITEMS.map((entry) => [entry.id, entry.a]));
  const spine = inputs.itemsDoc.SPINE;
  const placements: JudgePlacement[] = [];
  for (const run of inputs.canvas.runs) {
    for (const item of run.items) {
      const attrs = attrsById.get(item.id);
      if (!attrs) continue;
      placements.push({
        run: run.run,
        shelf: item.shelf,
        width: item.w * item.hf,
        path: spine.map((attr) => attrs[attr] ?? ""),
      });
    }
  }
  return placements;
};

const alongShelfShare = (members: readonly JudgePlacement[], depth: number): number | null => {
  if (new Set(members.map((member) => member.path[depth] ?? "")).size < 2) return null;
  const byShelf = new Map<string, JudgePlacement[]>();
  for (const member of members) {
    const key = `${member.run}|${member.shelf}`;
    const list = byShelf.get(key) ?? [];
    list.push(member);
    byShelf.set(key, list);
  }
  let shared = 0;
  let total = 0;
  byShelf.forEach((list) => {
    const width = list.reduce((sum, member) => sum + member.width, 0);
    total += width;
    if (new Set(list.map((member) => member.path[depth] ?? "")).size >= 2) shared += width;
  });
  return total > 0 ? shared / total : null;
};

const blocksAt = (
  placements: readonly JudgePlacement[],
  depth: number,
): Map<string, JudgePlacement[]> => {
  const byBlock = new Map<string, JudgePlacement[]>();
  for (const placement of placements) {
    const key = placement.path.slice(0, depth).join(PATH_SEPARATOR);
    const list = byBlock.get(key) ?? [];
    list.push(placement);
    byBlock.set(key, list);
  }
  return byBlock;
};

export const buildLevelJudge = (inputs: ResolverInputs): LevelOrientationJudge => {
  const placements = placementsOf(inputs);
  return (depth, _attr, declared) => {
    let shared = 0;
    let total = 0;
    blocksAt(placements, depth).forEach((members) => {
      const share = alongShelfShare(members, depth);
      if (share == null) return;
      const width = members.reduce((sum, member) => sum + member.width, 0);
      shared += share * width;
      total += width;
    });
    if (total === 0) return "drop";
    const measured = shared / total >= ALONG_SHELF_SHARE ? "horizontal" : "vertical";
    return measured === declared ? "drop" : "keep";
  };
};

export const buildContraryJudge = (inputs: ResolverInputs): ContraryBlockJudge => {
  const shelvesPerRun = new Map(
    inputs.canvas.runs.map((run, index) => [
      run.run,
      inputs.canvas.geo[index]?.shelves.length || 1,
    ]),
  );
  const placements = placementsOf(inputs);

  return (depth, blockKey, declared) => {
    const members = placements.filter(
      (placement) => placement.path.slice(0, depth).join(PATH_SEPARATOR) === blockKey,
    );
    if (!members.length) return "keep";
    const byValue = new Map<string, JudgePlacement[]>();
    for (const member of members) {
      const value = member.path[depth] ?? "";
      const list = byValue.get(value) ?? [];
      list.push(member);
      byValue.set(value, list);
    }
    if (byValue.size < 2) return "drop";
    if (declared !== "horizontal") return "keep";
    const share = alongShelfShare(members, depth);
    if (share != null && share >= ALONG_SHELF_SHARE) return "drop";
    // Full-height columns are along-x too; pair centroids misread wide columns as bands.
    let coveredWidth = 0;
    let totalWidth = 0;
    byValue.forEach((group) => {
      const shelvesByRun = new Map<number, Set<number>>();
      let width = 0;
      for (const member of group) {
        const set = shelvesByRun.get(member.run) ?? new Set<number>();
        set.add(member.shelf);
        shelvesByRun.set(member.run, set);
        width += member.width;
      }
      let coverage = 0;
      let runsUsed = 0;
      shelvesByRun.forEach((set, run) => {
        coverage += set.size / Math.max(shelvesPerRun.get(run) ?? 1, 1);
        runsUsed += 1;
      });
      totalWidth += width;
      if (runsUsed) coveredWidth += width * Math.min(coverage / runsUsed, 1);
    });
    return totalWidth > 0 && coveredWidth / totalWidth >= inputs.verticalBlockShare
      ? "drop"
      : "keep";
  };
};

export const bandedDepthOf = (inputs: ResolverInputs): number =>
  inputs.itemsDoc.SPINE.findIndex((attr) => inputs.itemsDoc.ORIENT[attr] === "vertical");

export const buildJudges = (inputs: ResolverInputs): InferenceJudges => ({
  contrary: buildContraryJudge(inputs),
  level: buildLevelJudge(inputs),
  bandedDepth: bandedDepthOf(inputs),
  quantityAttribute: inputs.quantityAttribute,
  skuIdsForAttrValues: (attr, values) => {
    const wanted = new Set(values);
    return inputs.itemsDoc.ITEMS.filter((entry) => wanted.has(String(entry.a[attr] ?? ""))).map(
      (entry) => entry.id,
    );
  },
});

export const useInference = (inputs: ResolverInputs): InferenceApi => {
  const report = useMemo(() => {
    try {
      return inferCdt(inputs.canvas, inputs.itemsDoc);
    } catch {
      return null;
    }
  }, [inputs]);
  const summary = useMemo(() => {
    if (!report) return EMPTY;
    try {
      return summarizeInference(report, buildJudges(inputs));
    } catch {
      return summarizeInference(report);
    }
  }, [report, inputs]);
  return { report, ...summary };
};
